
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useCookieConsentLogic, CookiePreferences } from '@/hooks/useCookieConsentLogic';
import { CookieBannerSimple } from './cookie-consent/CookieBannerSimple';
import { CookieBannerDetailed } from './cookie-consent/CookieBannerDetailed';
import { CookieSettingsButton } from './cookie-consent/CookieSettingsButton';

// Export types for backward compatibility
export type { CookiePreferences };

// Export hook for backward compatibility
export const useCookieConsent = useCookieConsentLogic;

// Composant de bannière de consentement
const CookieConsent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { hasConsented, preferences, setPreferences, savePreferences, acceptAll, acceptNecessary } = useCookieConsentLogic();
  const location = useLocation();

  // Vérifier si la bannière doit être affichée
  useEffect(() => {
    if (!hasConsented) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000); // Afficher après 1 seconde
      return () => clearTimeout(timer);
    } else {
      // Si l'utilisateur a déjà consenti, s'assurer que la bannière est fermée
      setIsOpen(false);
    }
  }, [hasConsented]);

  // `finally` : le bandeau doit pouvoir se fermer MÊME si l'enregistrement échoue.
  // Un consentement qu'on ne peut pas refuser n'en est pas un — et un écran bloqué
  // par un bandeau non refusable serait un défaut de conformité, pas seulement une gêne.
  const handleAcceptNecessary = () => {
    try {
      acceptNecessary();
    } finally {
      setIsOpen(false);
      setShowDetails(false);
    }
  };

  const handleAcceptAll = () => {
    try {
      acceptAll();
    } finally {
      setIsOpen(false);
      setShowDetails(false);
    }
  };

  const handleSavePreferences = () => {
    try {
      savePreferences(preferences);
      toast.success('Vos préférences ont été enregistrées', { duration: 3000 });
    } finally {
      setShowDetails(false);
      setIsOpen(false);
    }
  };

  const handleOpenPreferences = () => {
    setShowDetails(false);
    setIsOpen(true);
  };

  // Masqué dans l'admin : le cadre de consentement concerne le site public,
  // pas le back-office (cookies fonctionnels/auth).
  if (location.pathname.startsWith('/admin')) return null;

  return createPortal(
    <>



      {/* Bannière de consentement */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            // Repère pour le pré-rendu SEO. Ce bandeau se rend dans un PORTAIL, donc en
            // enfant direct de <body> : puppeteer le sérialisait avec la page et il
            // finissait écrit en dur dans les snapshots de public/. Le visiteur recevait
            // alors le bandeau figé du fichier statique, puis React montait le sien
            // par-dessus — deux bandeaux, illisibles sur mobile, dont un mort.
            // scripts/retirer-bandeau-cookies.mjs s'appuie sur cet attribut.
            data-cookie-banner=""
            // Interdit à Google d'utiliser ce texte comme extrait de résultat. Le retrait du
            // pré-rendu ne suffisait pas : Googlebot EXÉCUTE le JavaScript, React monte le
            // bandeau au bout d'une seconde, et l'indexeur le voit — vérifié le 03/11/2026
            // avec l'user-agent du robot, sur 6 pages sur 6. Google avait déjà choisi « Votre
            // choix sera conservé pendant 13 mois maximum. Personnaliser Refuser Accepter
            // tout » comme description de /anniversaire-prestige-paris et de la demande en
            // mariage. Couvre les deux états (simple et détaillé), d'où sa place ici.
            data-nosnippet=""
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'tween', duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            // paddingBottom en style inline ÉCRASAIT le py-3 de Tailwind. Sur tout appareil
            // sans encoche, l'inset vaut 0 : le dernier bouton se retrouvait collé au bord
            // exact de l'écran, sans un pixel de marge (mesuré à 0 px en 375x812), là où le
            // navigateur mobile dessine sa propre barre. On rétablit la marge, puis on ajoute
            // l'encoche par-dessus.
            style={{ position: 'fixed', bottom: 0, left: 0, right: 0, paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
            className="z-[9999] px-4 py-3 bg-background/95 backdrop-blur-sm border-t border-border shadow-[0_-4px_24px_rgba(0,0,0,0.06)]"
          >
            <div className="container mx-auto max-w-7xl">
              {!showDetails ? (
                <CookieBannerSimple
                  onPersonalize={() => setShowDetails(true)}
                  onAcceptNecessary={handleAcceptNecessary}
                  onAcceptAll={handleAcceptAll}
                />
              ) : (
                <CookieBannerDetailed
                  preferences={preferences}
                  setPreferences={setPreferences}
                  onSavePreferences={handleSavePreferences}
                  onAcceptNecessary={handleAcceptNecessary}
                  onBack={() => setShowDetails(false)}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
};

export default CookieConsent;
